import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getMyAlbum,
  listWorldCupPlayers,
  openStickerPack,
  type StickerPackKind,
} from '@/lib/data/provider';

/** The full World Cup sticker pool (every WC player). Reference-cached. */
export function useStickerPool() {
  return useQuery({
    queryKey: ['album', 'pool'],
    queryFn: listWorldCupPlayers,
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

/** The caller's owned stickers + pool size + whether the free pack is available. */
export function useMyAlbum() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['album', 'mine', userId ?? 'anon'],
    queryFn: getMyAlbum,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** Open a pack (free daily or coin-bought); refreshes the album + the wallet. */
export function useOpenPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: StickerPackKind) => openStickerPack(kind),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['album', 'mine'] });
      // A paid pack debits coins - refresh the wallet so balances stay in sync.
      queryClient.invalidateQueries({ queryKey: ['rewards', 'wallet'] });
    },
  });
}
